import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  AutocompleteInput,
  FormDataConsumer,
  RadioButtonGroupInput,
  regex,
  required,
  SaveButton,
  SelectInput,
  TextInput,
  useNotify,
} from 'react-admin';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Link, useLocation } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
} from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';

import { WELLNESS_QF_BASE_URL } from '@pumpkincare/config';
import {
  ChainIcon,
  COUNTRIES_LIST,
  ProcessingStep,
  ToggleButtonInput,
} from '@pumpkincare/shared';
import { useCheckEmail } from '@pumpkincare/users';

import { stateList } from '../../../../../lib/shared';
import RoutePaths from '../../../../../routes';
import OutlinedVetInput from '../../../../../shared/components/outlined-vet-input';
import PlanSummary from '../../../../plans/plan-summary';
// really need to fix circular dependencies
import {
  HDYHAU_OPTIONS_WELLNESS,
  MY_VET_CLINIC,
} from '../../../../quotes/quoteFlow/constants/HDYHAUQuestions';
import { countryToFlag } from '../../../../quotes/quoteFlow/utils/quoteUtils';
import AddressDetails from '../../../../quotes/quoteFlow/view/AddressDetails';

const styleOverwrite = {
  base: {
    fontSize: '16px',
    fontFamily: 'Roboto',
    color: 'rgba(0, 0, 0, 0.87)',
  },
};
const StripeInput = forwardRef((props, ref) => {
  const { component: Component, ...rest } = props;

  const focusCallback = useCallback(
    () => ({
      focus: () => ref.current.focus,
    }),
    []
  );
  const onComponentReady = useCallback(element => (ref.current = element), []);

  useImperativeHandle(ref, focusCallback);

  return (
    <Component
      disableLink
      onReady={onComponentReady}
      options={{ placeholder: '', style: styleOverwrite }}
      {...rest}
    />
  );
});

const INITIAL_STRIPE_STATE = {
  empty: true,
  error: {},
  complete: false,
};

function effectStripeState(element, key, setStripeState) {
  element.on('change', event => {
    setStripeState(prevState => ({
      ...prevState,
      [key]: {
        empty: event.empty,
        error: event.error,
        complete: event.complete,
      },
    }));
  });
}

function CountryOption(country) {
  return (
    <>
      <span style={{ marginRight: '4px' }}>{countryToFlag(country.id)}</span>
      {country.name}
    </>
  );
}

function WellnessQuoteTabCheckout({ quote, selectedPlans }) {
  const notify = useNotify();
  const elements = useElements();
  const cardNumberElement = elements.getElement(CardNumberElement);
  const cardExpiryElement = elements.getElement(CardExpiryElement);
  const cardCvcElement = elements.getElement(CardCvcElement);

  const [stripeState, setStripeState] = useState({
    cardNumber: INITIAL_STRIPE_STATE,
    cardExpiry: INITIAL_STRIPE_STATE,
    cardCvc: INITIAL_STRIPE_STATE,
  });

  useEffect(() => {
    if (cardNumberElement && cardExpiryElement && cardCvcElement) {
      [
        { key: 'cardNumber', element: cardNumberElement },
        { key: 'cardExpiry', element: cardExpiryElement },
        { key: 'cardCvc', element: cardCvcElement },
      ].forEach(({ key, element }) =>
        effectStripeState(element, key, setStripeState)
      );
    }
  }, [cardNumberElement, cardExpiryElement, cardCvcElement]);

  const { state: routerState } = useLocation();
  const { isAddAPet, user } = useCheckEmail();

  return (
    <div
      style={{
        background: 'white',
        padding: '40px 8px',
        width: '100%',
      }}
    >
      <ProcessingStep
        steps={['Details', 'Choose Plan', 'Checkout']}
        currentStep={3}
        style={{
          width: '63vw',
          minWidth: '500px',
          margin: '0 auto 40px',
        }}
      />

      <FormDataConsumer>
        {({ formData }) => {
          return (
            <Grid container justifyContent='space-between'>
              <Grid item xs={12} sm={7}>
                {isAddAPet ? (
                  <Box>
                    <Typography variant='body1'>
                      Here is the existing address and payment method associated with
                      this account.
                    </Typography>
                    <Typography
                      variant='body1'
                      sx={{ color: '#EA0404', marginBottom: 2 }}
                    >
                      If you would like to make changes, note that this will update
                      for all pet plans.
                    </Typography>

                    <Button
                      component={Link}
                      to={{
                        pathname: `/${RoutePaths.customers}/${user.id}/show`,
                        state: routerState,
                      }}
                      sx={{ color: '#5868F5', padding: 0, marginBottom: 4 }}
                    >
                      Edit Details
                    </Button>
                    {!user.payment_details ? (
                      <Box>
                        <Typography variant='h5' sx={{ marginBottom: 3 }}>
                          Payment Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextInput
                              name='name_on_card'
                              source='name_on_card'
                              label='Name on Card'
                              variant='outlined'
                              validate={[required()]}
                              size='medium'
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              required
                              label='Card Number'
                              variant='outlined'
                              size='medium'
                              fullWidth
                              InputLabelProps={{
                                shrink: !stripeState.cardNumber.empty || undefined,
                              }}
                              InputProps={{
                                inputComponent: StripeInput,
                                inputProps: { component: CardNumberElement },
                              }}
                              error={!!stripeState.cardNumber.error?.message}
                              helperText={
                                stripeState.cardNumber.error?.message || ' '
                              }
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              required
                              label='Expiration Date (MM/YY)'
                              variant='outlined'
                              size='medium'
                              fullWidth
                              InputLabelProps={{
                                shrink: !stripeState.cardExpiry.empty || undefined,
                              }}
                              InputProps={{
                                inputComponent: StripeInput,
                                inputProps: { component: CardExpiryElement },
                              }}
                              error={!!stripeState.cardExpiry.error?.message}
                              helperText={
                                stripeState.cardExpiry.error?.message || ' '
                              }
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              required
                              label='CVC'
                              variant='outlined'
                              size='medium'
                              fullWidth
                              InputLabelProps={{
                                shrink: !stripeState.cardCvc.empty || undefined,
                              }}
                              InputProps={{
                                inputComponent: StripeInput,
                                inputProps: { component: CardCvcElement },
                              }}
                              error={!!stripeState.cardCvc.error?.message}
                              helperText={stripeState.cardCvc.error?.message || ' '}
                            />
                          </Grid>
                        </Grid>
                        {!user?.billing_address ? (
                          <>
                            <Typography variant='subtitle1' sx={{ mb: 1, mt: 4 }}>
                              Billing Address *
                            </Typography>
                            <RadioButtonGroupInput
                              source='isBillingSameAsShipping'
                              sx={{ '& .MuiFormLabel-root': { display: 'none' } }}
                              aria-label='Is the billing address the same as shipping address'
                              row={false}
                              choices={[
                                { id: 'true', name: 'Same as shipping address' },
                                {
                                  id: 'false',
                                  name: 'Use a different billing address',
                                },
                              ]}
                              validate={[required()]}
                            />
                            {formData.isBillingSameAsShipping === 'false' && (
                              <Grid container spacing={2} sx={{ mt: 2 }}>
                                <Grid item xs={12}>
                                  <AutocompleteInput
                                    source={'billing_information.country'}
                                    label='Country'
                                    variant='outlined'
                                    size='medium'
                                    choices={COUNTRIES_LIST}
                                    validate={[required()]}
                                    inputText={country => country.name}
                                    optionText={CountryOption}
                                    optionValue='id'
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextInput
                                    name='billing_information.street_1'
                                    source='billing_information.street_1'
                                    label='Address'
                                    variant='outlined'
                                    size='medium'
                                    validate={[required()]}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextInput
                                    name='billing_information.street_2'
                                    source='billing_information.street_2'
                                    label='Address Line 2 (Optional)'
                                    variant='outlined'
                                    size='medium'
                                    defaultValue=''
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextInput
                                    name='billing_information.city'
                                    source='billing_information.city'
                                    label='City'
                                    variant='outlined'
                                    size='medium'
                                    validate={[required()]}
                                    fullWidth
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  {formData.billing_information?.country === 'US' ? (
                                    <SelectInput
                                      source='billing_information.state'
                                      label='State'
                                      variant='outlined'
                                      size='medium'
                                      validate={[required()]}
                                      choices={stateList}
                                      fullWidth
                                    />
                                  ) : (
                                    <TextInput
                                      name='billing_information.state'
                                      source='billing_information.state'
                                      label='State'
                                      variant='outlined'
                                      size='medium'
                                      validate={[required()]}
                                      fullWidth
                                    />
                                  )}
                                </Grid>
                                <Grid item xs={6}>
                                  <TextInput
                                    name='billing_information.zipcode'
                                    source='billing_information.zipcode'
                                    label='Zip Code'
                                    variant='outlined'
                                    size='medium'
                                    validate={[required()]}
                                    fullWidth
                                  />
                                </Grid>
                              </Grid>
                            )}
                          </>
                        ) : null}
                      </Box>
                    ) : (
                      <AddressDetails existingUserDetails={user} />
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant='h5' sx={{ marginBottom: 3, marginTop: 4 }}>
                      Shipping Address
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          value={formData.first_name}
                          label='First Name'
                          variant='outlined'
                          size='medium'
                          disabled
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          value={formData.last_name}
                          label='Last Name'
                          variant='outlined'
                          size='medium'
                          disabled
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextInput
                          name='shipping_information.street_1'
                          source='shipping_information.street_1'
                          label='Address'
                          variant='outlined'
                          size='medium'
                          validate={[required()]}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextInput
                          name='shipping_information.street_2'
                          source='shipping_information.street_2'
                          label='Address Line 2 (Optional)'
                          variant='outlined'
                          size='medium'
                          defaultValue=''
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextInput
                          name='shipping_information.city'
                          source='shipping_information.city'
                          label='City'
                          variant='outlined'
                          size='medium'
                          validate={[required()]}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <SelectInput
                          source='shipping_information.state'
                          label='State'
                          variant='outlined'
                          size='medium'
                          validate={[required()]}
                          choices={stateList}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInput
                          name='shipping_information.zipcode'
                          source='shipping_information.zipcode'
                          label='Zip Code'
                          variant='outlined'
                          size='medium'
                          validate={[
                            required(),
                            regex(/^\d{5}$/, 'Must be a valid Zip Code'),
                          ]}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Typography variant='h5' sx={{ marginBottom: 3, marginTop: 4 }}>
                      Contact Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          value={formData.email}
                          label='Email Address'
                          variant='outlined'
                          size='medium'
                          disabled
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextInput
                          name='phone'
                          source='phone'
                          label='Phone Number'
                          variant='outlined'
                          size='medium'
                          validate={[required()]}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Typography variant='h5' sx={{ marginBottom: 3, marginTop: 4 }}>
                      Payment Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextInput
                          name='name_on_card'
                          source='name_on_card'
                          label='Name on Card'
                          variant='outlined'
                          validate={[required()]}
                          size='medium'
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          label='Card Number'
                          variant='outlined'
                          size='medium'
                          fullWidth
                          InputLabelProps={{
                            shrink: !stripeState.cardNumber.empty || undefined,
                          }}
                          InputProps={{
                            inputComponent: StripeInput,
                            inputProps: { component: CardNumberElement },
                          }}
                          error={!!stripeState.cardNumber.error?.message}
                          helperText={stripeState.cardNumber.error?.message || ' '}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          required
                          label='Expiration Date (MM/YY)'
                          variant='outlined'
                          size='medium'
                          fullWidth
                          InputLabelProps={{
                            shrink: !stripeState.cardExpiry.empty || undefined,
                          }}
                          InputProps={{
                            inputComponent: StripeInput,
                            inputProps: { component: CardExpiryElement },
                          }}
                          error={!!stripeState.cardExpiry.error?.message}
                          helperText={stripeState.cardExpiry.error?.message || ' '}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          required
                          label='CVC'
                          variant='outlined'
                          size='medium'
                          fullWidth
                          InputLabelProps={{
                            shrink: !stripeState.cardCvc.empty || undefined,
                          }}
                          InputProps={{
                            inputComponent: StripeInput,
                            inputProps: { component: CardCvcElement },
                          }}
                          error={!!stripeState.cardCvc.error?.message}
                          helperText={stripeState.cardCvc.error?.message || ' '}
                        />
                      </Grid>
                    </Grid>

                    <Typography variant='subtitle1' sx={{ mb: 1, mt: 4 }}>
                      Billing Address *
                    </Typography>
                    <RadioButtonGroupInput
                      source='isBillingSameAsShipping'
                      sx={{ '& .MuiFormLabel-root': { display: 'none' } }}
                      aria-label='Is the billing address the same as shipping address'
                      row={false}
                      choices={[
                        { id: 'true', name: 'Same as shipping address' },
                        { id: 'false', name: 'Use a different billing address' },
                      ]}
                      validate={[required()]}
                    />
                    {formData.isBillingSameAsShipping === 'false' && (
                      <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12}>
                          <AutocompleteInput
                            source={'billing_information.country'}
                            label='Country'
                            variant='outlined'
                            size='medium'
                            choices={COUNTRIES_LIST}
                            validate={[required()]}
                            inputText={country => country.name}
                            optionText={CountryOption}
                            optionValue='id'
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextInput
                            name='billing_information.street_1'
                            source='billing_information.street_1'
                            label='Address'
                            variant='outlined'
                            size='medium'
                            validate={[required()]}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextInput
                            name='billing_information.street_2'
                            source='billing_information.street_2'
                            label='Address Line 2 (Optional)'
                            variant='outlined'
                            size='medium'
                            defaultValue=''
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextInput
                            name='billing_information.city'
                            source='billing_information.city'
                            label='City'
                            variant='outlined'
                            size='medium'
                            validate={[required()]}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          {formData.billing_information?.country === 'US' ? (
                            <SelectInput
                              source='billing_information.state'
                              label='State'
                              variant='outlined'
                              size='medium'
                              validate={[required()]}
                              choices={stateList}
                              fullWidth
                            />
                          ) : (
                            <TextInput
                              name='billing_information.state'
                              source='billing_information.state'
                              label='State'
                              variant='outlined'
                              size='medium'
                              validate={[required()]}
                              fullWidth
                            />
                          )}
                        </Grid>
                        <Grid item xs={6}>
                          <TextInput
                            name='billing_information.zipcode'
                            source='billing_information.zipcode'
                            label='Zip Code'
                            variant='outlined'
                            size='medium'
                            validate={[required()]}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Typography variant='h5' sx={{ marginBottom: 3, marginTop: 4 }}>
                      Referral
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <SelectInput
                          source='referral'
                          choices={HDYHAU_OPTIONS_WELLNESS}
                          optionText='label'
                          optionValue='value'
                          variant='outlined'
                          size='medium'
                          validate={required()}
                          label='How did you hear about Pumpkin Wellness Club?'
                          fullWidth
                        />
                      </Grid>
                      {formData.referral === MY_VET_CLINIC && (
                        <Grid item xs={12}>
                          <OutlinedVetInput
                            source='vet'
                            formData={formData}
                            zipcode={formData.zipcode}
                            validate={required()}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <Grid container gap='0 16px' justifyContent='space-between'>
                  <Button sx={{ color: '#000000' }}>QUOTE SUMMARY</Button>

                  <CopyToClipboard
                    text={`${WELLNESS_QF_BASE_URL}/?quoteId=${quote.id}`}
                    onCopy={() =>
                      notify('Quote copied to clipboard', { type: 'success' })
                    }
                  >
                    <Button sx={{ color: '#5868F5', gap: '0 8px' }}>
                      <ChainIcon />
                      COPY QUOTE
                    </Button>
                  </CopyToClipboard>

                  <Button
                    component={Link}
                    to={{
                      pathname: `/${RoutePaths.wellnessQuote}/create`,
                    }}
                    sx={{ color: '#5868F5', gap: '0 8px' }}
                  >
                    <EditIcon />
                    EDIT
                  </Button>
                </Grid>

                <ToggleButtonInput
                  inputOptions={{
                    source: 'billed_annually',
                    validate: [required()],
                    disabled: isAddAPet,
                  }}
                  toggleOptions={[
                    { value: 'false', label: 'MONTHLY' },
                    { value: 'true', label: 'ANNUAL' },
                  ]}
                  sx={{
                    group: {
                      marginTop: '24px',
                      display: 'flex',
                      justifyContent: 'center',
                    },
                  }}
                />

                <PlanSummary
                  pets={(quote.quote_pets || []).map(pet => ({
                    ...pet,
                    wellnessPlan: selectedPlans[pet.id],
                  }))}
                  isChargedAnnually={
                    user?.is_charged_annually || formData.billed_annually === 'true'
                  }
                  hasFeeShown
                  sx={{ width: '100%' }}
                />

                <SaveButton
                  label='CHECKOUT'
                  variant='contained'
                  color='primary'
                  icon={null}
                  sx={{ width: '100%' }}
                  type='button'
                />
              </Grid>
            </Grid>
          );
        }}
      </FormDataConsumer>
    </div>
  );
}

StripeInput.propTypes = {
  component: PropTypes.func.isRequired,
};
CountryOption.propTypes = {
  country: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};

WellnessQuoteTabCheckout.propTypes = {
  quote: PropTypes.shape({
    id: PropTypes.string,
    quote_pets: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  selectedPlans: PropTypes.object.isRequired,
};

export default WellnessQuoteTabCheckout;
